import { useEffect, useState } from "react";
import { useDataProvider, useRecordContext } from "react-admin";
import { get } from "lodash";
import { Typography } from "@mui/material";
import {
  getStreamIdsForCategories,
  getTapTestStreamParams,
  getDataAvailabilityConfiguration
} from "src/providers/streams/runeStreamsUtils";
import { DataRecencyField } from "./DataRecencyField";
import { deriveTimestampsFromDate } from "src/utils/dateTimeUtils";
import { TimeInterval } from "src/constants";
import { ProjectDisplayConfig } from "../resources/project/ProjectShow/ProjectDisplayConfigurationDialog";

// Kept separate from the field so the page CSV export uses the identical
// stream request and count semantics as the displayed cell.
type TapTestDataProvider = {
  getStream: (
    id: string,
    params: { startTime: string; endTime: number; format: string }
  ) => Promise<{ json?: { cardinality?: number } }>;
  getStreamIds: (params: unknown) => Promise<unknown>;
};

export const getTapTestCount = async (
  dataProvider: unknown,
  patientId: string | number,
  projectStart: Date
): Promise<number> => {
  const streamsDataProvider = dataProvider as TapTestDataProvider;
  const { startDate } = getDataAvailabilityConfiguration(
    TimeInterval.NINETY_DAYS,
    projectStart
  );
  const { startDateTimestamp, endDateTimestamp } =
    deriveTimestampsFromDate(startDate);
  const tapTest: (keyof ProjectDisplayConfig["dataTypes"])[] = ["tap_test"];
  const streamIdsResults = await getStreamIdsForCategories({
    dataProvider: streamsDataProvider,
    desiredCategories: tapTest,
    enabledCategories: tapTest,
    patientId,
    streamParamsFunctions: { tap_test: getTapTestStreamParams }
  });
  const stream = streamIdsResults[0]?.streamIds?.data;

  if (!stream || stream.length === 0) {
    return 0;
  }
  if (stream.length > 1) {
    throw new Error("Expected one tap test stream at most");
  }

  const result = await streamsDataProvider.getStream(stream[0].id, {
    startTime: JSON.stringify(startDateTimestamp),
    endTime: endDateTimestamp,
    format: "json"
  });
  return result?.json?.cardinality || 0;
};

const TapTestField = (props: {
  projectStart: Date;
  source: string;
  label: string;
  category: string;
}) => {
  const { projectStart, source, label, category } = props;
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const [testCount, setTestCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const patientId = get(record, "id");

  useEffect(() => {
    const fetchStreamData = async () => {
      getTapTestCount(dataProvider, patientId, projectStart)
        .then((count) => setTestCount(count))
        .catch((error) => {
          console.error("Error fetching tap test data", { error });
          setError(error);
          return error;
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchStreamData();
  }, [dataProvider, patientId]);

  if (loading) {
    return <Typography variant="body2">Loading...</Typography>;
  }

  return (
    <DataRecencyField
      {...{ source, label, category, includeComplianceFlag: false }}
    >
      {!error && testCount > 0 && (
        <Typography component="div" variant="body2">
          {testCount} in 90 days
        </Typography>
      )}
    </DataRecencyField>
  );
};

export default TapTestField;
